import type {ThunkResult} from '../../types/redux';
import {parsedWorkspaceSpec} from '../../util/parseWorkspaceSpec';
import type * as Actions from './actions';
import * as ActionsInternal from './actionsInternal';
import * as Api from './api';
import type * as Types from './types';

// Assumes startAction has already been dispatched
export function loadMetadataList(
  startAction: ReturnType<typeof Actions.loadMetadataListStarted>
): ThunkResult<Promise<Api.LoadMetadataListResultType>> {
  return (dispatch, getState, client) => {
    return Api.loadMetadataList(client, startAction.payload.params).then(
      views => {
        dispatch(
          ActionsInternal.loadMetadataListFinished(
            startAction.payload.id,
            views
          )
        );
        return Promise.resolve(views);
      }
    );
  };
}

export function load(
  cid: string
): ThunkResult<Promise<{type: string; username: string}>> {
  return async (dispatch, getState, client) => {
    const state = getState();
    const {id: serverID, partRef: oldViewRef} = state.views.views[cid] ?? {};
    if (serverID == null) {
      // We don't have a server ID if the view is newly created, and the initial
      // save for it is currently in progress.
      if (oldViewRef != null) {
        // If its newly created, then we already have the spec locally. So don't
        // initiate a new load.
        const view = state.views.views[cid];
        return {type: view.type, username: view.user.username};
      }
      throw new Error('Tried to load view without serverID');
    }
    dispatch(ActionsInternal.loadStarted());

    const result = await Api.loadSingleViewById(client, serverID, 'cache');

    // parse workspace view spec and apply any necessary migrations
    if (
      result.type !== 'project-view' &&
      result.type !== 'group-view' &&
      result.type !== 'sweep-view' &&
      result.type !== 'run-view'
    ) {
      throw new Error(`Loaded workspace has incorrect type: ${result.type}`);
    }
    const parsedSpec = parsedWorkspaceSpec(result.spec, result.type);

    dispatch(
      ActionsInternal.loadFinished(
        cid,
        {...result, spec: parsedSpec, cid}, // keep the current client ID
        true
      )
    );

    return {type: result.type, username: result.user.username};
  };
}

export function deleteObject(ref: Types.AllPartRefs): ThunkResult<void> {
  return dispatch => {
    dispatch(ActionsInternal.deleteHistoryForObject(ref));
    // TODO(someone with spare time): This is our brilliant garbage collection scheme.
    // In order to avoid having selectors crash when they use stale references to grab
    // now-deleted state, we employ the brilliant strategy of...deleting stuff on a time
    // delay. 5 seconds should be plenty to allow react to update/redraw components.
    setTimeout(() => dispatch(ActionsInternal.deleteObject(ref)), 5000);
  };
}

export function updateViewSpec(viewID: string, spec: any): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const {partRef: oldPartRef} = state.views.views[viewID] ?? {};
    dispatch(ActionsInternal.updateViewSpec(viewID, spec));
    if (oldPartRef != null) {
      dispatch(deleteObject(oldPartRef));
    }
  };
}

// TODO(views): find a better way to callback from the delete
export function del(
  ref: Types.ViewRef,
  then?: () => void
): ThunkResult<Promise<Api.DeleteResultType>> {
  return (dispatch, getState, client) => {
    const state = getState();
    const {id: serverID} = state.views.views[ref.id] ?? {};
    if (serverID == null) {
      throw new Error('Tried to delete view without serverID');
    }
    const res = Api.deleteView(client, serverID);
    if (then) {
      res.then(then);
    }
    return res;
  };
}

export function unloadMetadataList(metadaListID: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const {viewIds: viewsInList} = state.views.lists[metadaListID] ?? {};

    dispatch(ActionsInternal.unloadMetadataList(metadaListID));
    for (const view of viewsInList) {
      dispatch(unloadView(view));
    }
  };
}

export function unloadView(viewID: string): ThunkResult<void> {
  return (dispatch, getState) => {
    const state = getState();
    const view = state.views.views[viewID];

    if (view) {
      dispatch(ActionsInternal.unloadView(viewID));
      if (view.partRef != null) {
        dispatch(deleteObject(view.partRef));
      }
    }
  };
}

export function starView(ref: Types.ViewRef): ThunkResult<void> {
  return (dispatch, getState, client) => {
    const state = getState();
    const {id: serverID} = state.views.views[ref.id] ?? {};
    if (serverID == null) {
      throw new Error('Tried to star view without serverID');
    }

    dispatch(ActionsInternal.starViewStarted(ref.id));
    Api.starView(client, serverID).then(starCount => {
      dispatch(ActionsInternal.starViewFinished(ref.id, starCount));
    });
  };
}

export function unstarView(ref: Types.ViewRef): ThunkResult<void> {
  return (dispatch, getState, client) => {
    const state = getState();
    const {id: serverID} = state.views.views[ref.id] ?? {};
    if (serverID == null) {
      throw new Error('Tried to unstar view without serverID');
    }

    dispatch(ActionsInternal.unstarViewStarted(ref.id));
    Api.unstarView(client, serverID).then(starCount => {
      dispatch(ActionsInternal.unstarViewFinished(ref.id, starCount));
    });
  };
}
